<template>
  <div
    ref="container"
    class="menu"
    :class="containerModifierClass"
    @keydown.esc.capture="rememberOpenOnEscape"
    @keydown.esc="onKeyDownEscape"
  >
    <div v-if="!isPermanent" ref="activator" @click.prevent.stop="onActivatorClick">
      <slot name="activator" class="menu-activator" />
    </div>

    <sp-bottom-sheet
      v-if="showBottomSheet"
      :label="label"
      :open="isActive"
      :no-restore-focus="noRestoreFocus"
      :permanent="isPermanent"
      :expand-height-max="mobileExpandHeightMax"
      @close="onSurfaceClosed"
      @click="onContentClick"
    >
      <slot />
      <slot slot="action" name="sheet-action">
        <sp-button class="bottom-sheet-close-action" color="primary">{{ t("menu_close") }}</sp-button>
      </slot>
    </sp-bottom-sheet>

    <sp-popup
      v-else
      ref="popup"
      :active="isActive"
      :no-restore-focus="noRestoreFocus"
      :location="location"
      :no-flip="noFlip"
      :pointer="pointer"
      :permanent="isPermanent"
      :disabled="disabled"
      :anchor.prop="activator"
      @click="onContentClick"
      @open="onPopupShown"
      @close="onSurfaceClosed"
    >
      <slot />
    </sp-popup>
  </div>
</template>

<script setup>
/**
 * The SpMenu component shows a menu at the position of the element used to activate it.
 *
 * @displayName Menu
 * @group Custom Elements
 * @component sp-menu
 *
 * @slot default - The menu's content.
 * @slot activator - The element that opens the menu when clicked.
 * @slot sheet-action - The bottom sheet's action, below the sm breakpoint. Defaults to a close
 *   button.
 *
 * The `open` attribute controls declarative state. The host `show()` and `hide()` methods control
 * imperative state.
 */
import { computed, ref, toRef, useTemplateRef } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useDisabled } from "#ce/composables/useDisabled";
import { t } from "#ce/shared/components-i18n";
import { toBoolean } from "#ce/utils/props";
import { props as menuProps } from "./props.js";
import { useMenuClicks } from "./composables/useMenuClicks";
import { useMenuEscape } from "./composables/useMenuEscape";
import { useMenuFocusReturn } from "./composables/useMenuFocusReturn";
import { useMenuOpenState } from "./composables/useMenuOpenState";
import { useMenuOutsideClick } from "./composables/useMenuOutsideClick";
import { useMenuVisibility } from "./composables/useMenuVisibility";

defineOptions({ inheritAttrs: false });

const emit = defineEmits(["input", "open", "close", "update-open"]);

const props = defineProps(menuProps);

const container = useTemplateRef("container");
const activator = useTemplateRef("activator");
const popup = useTemplateRef("popup");

const isPermanent = ref(toBoolean(props.permanent));
const nativeContentClicks = computed(() => toBoolean(props.nativeContentClicks));
const closeOnContentClick = computed(() => toBoolean(props.closeOnContentClick));
const closeOnOutsideClick = computed(() => toBoolean(props.closeOnOutsideClick));
const noFlip = computed(() => toBoolean(props.noFlip));
const { disabled } = useDisabled(toRef(props, "disabled"));

const { isGtSmScreen, ltModifiers } = useBreakpoints();
const containerModifierClass = computed(() => [...ltModifiers.value]);
const showBottomSheet = computed(() => !isPermanent.value && !isGtSmScreen.value && !toBoolean(props.noSheet));

const { isActive, onPopupShown, onSurfaceClosed } = useMenuOpenState({
  open: () => props.open,
  emit,
  returnFocus: () => returnFocus(),
});

useMenuVisibility({ isActive, disabled, isPermanent });

// `sp-bottom-sheet` returns focus itself.
const { activatorControl, markClosedByOutsideClick, returnFocus } = useMenuFocusReturn({
  isActive,
  activator,
  popup,
  skipRestore: () => showBottomSheet.value || toBoolean(props.noRestoreFocus),
});

const { onActivatorClick, onContentClick } = useMenuClicks({
  isActive,
  disabled,
  closeOnContentClick,
  nativeContentClicks,
  showBottomSheet,
  activator,
  activatorControl,
});

useMenuOutsideClick({ isActive, showBottomSheet, closeOnOutsideClick, container, markClosedByOutsideClick });

const { rememberOpenOnEscape, onKeyDownEscape } = useMenuEscape({ isActive, isPermanent, showBottomSheet });
</script>

<style>
:host {
  display: block;
  width: 100%;
}

:host([permanent]) {
  height: 100%;
}
</style>

<style scoped lang="scss">
/* Each `--sp-comp-menu-*` popup token sets the popup token it names. An unset one leaves the value a
   page sets on the host for that popup token, then the popup default. */
$popup-tokens: (
  "offset": "popup-offset",
  "offset-inline": "popup-offset-inline",
  "pointer-size": "popup-pointer-size",
  "pointer-offset": "popup-pointer-offset",
  "background-color": "popup-background-color",
  "box-shadow": "popup-box-shadow",
  "content-border-radius": "popup-content-border-radius",
);

.menu {
  @each $token, $inner in $popup-tokens {
    --menu-host-#{$inner}: var(--sp-comp-#{$inner});
  }

  position: relative;

  :host([permanent]) & {
    height: 100%;
  }
}
.menu-activator {
  cursor: pointer;
  display: block;
  position: relative;
}
sp-bottom-sheet {
  --bottom-sheet-border-radius: 8px 8px 0 0;
  --sp-comp-bottom-sheet-content-padding-inline: 0;
  --sp-comp-bottom-sheet-content-padding-block: var(--sp-comp-menu-sheet-content-padding-block, 0.5rem);
  --sp-comp-bottom-sheet-action-container-padding-inline: var(--sp-comp-menu-sheet-action-padding-inline, 0.5rem);
}
sp-popup {
  @each $token, $inner in $popup-tokens {
    --sp-comp-#{$inner}: var(--sp-comp-menu-#{$token}, var(--menu-host-#{$inner}));
  }
}
.bottom-sheet-close-action {
  --button-width: 100%;
}
</style>
